import {
  useInviteUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetPortfolioUsersQuery,
} from '@/api/portfolioApi';
import useServerPagination from '@/hooks/useServerPagination';
import useTimezone from '@/hooks/useTimezone';
import { showSuccess } from '@/utils/messageService';
import { PlusOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Card, Form, Space, Table } from 'antd';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { InviteUserModal } from '../Modal/InviteUserModal';
import { EditUserModal } from '../Modal/EditUserModal';
import DeleteUserModal from '../Modal/DeleteUserModal';
import { getUserTableColumns } from '../tableColumns';

const UsersTable = ({ portfolioId }) => {
  const [inviteUserModalVisible, setInviteUserModalVisible] = useState(false);
  const [editUserModalVisible, setEditUserModalVisible] = useState(false);
  const [deleteUserModalVisible, setDeleteUserModalVisible] = useState(false);
  const [selectedUserToEdit, setSelectedUserToEdit] = useState(null);
  const [selectedUserToDelete, setSelectedUserToDelete] = useState(null);
  const [userForm] = Form.useForm();
  const [editUserForm] = Form.useForm();
  const { timezone } = useTimezone();

  const { queryParams, paginationConfig, handleTableChange } =
    useServerPagination({ defaultSortBy: 'firstName' });

  const {
    data: response,
    isLoading,
    isFetching,
  } = useGetPortfolioUsersQuery(
    { portfolioId, ...queryParams },
    { skip: !portfolioId }
  );

  const users = response?.data || [];
  const totalElements = response?.pagination?.totalElements || 0;

  const [inviteUser, { isLoading: isInviting }] = useInviteUserMutation();
  const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();

  const handleInviteUser = async (values) => {
    try {
      await inviteUser({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        role: values.role,
        portfolioId,
      }).unwrap();
      userForm.resetFields();
      setInviteUserModalVisible(false);
      showSuccess('User invited successfully!');
    } catch (error) {
      console.error('Failed to create user:', error);
    }
  };

  const handleEditUserClick = useCallback(
    (user) => {
      setSelectedUserToEdit(user);
      editUserForm.setFieldsValue({
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        role: user.roles?.[0],
      });
      setEditUserModalVisible(true);
    },
    [editUserForm]
  );

  const handleEditUser = async (values) => {
    if (!selectedUserToEdit) return;
    try {
      await updateUser({
        userId: selectedUserToEdit.id,
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        role: values.role,
      }).unwrap();
      editUserForm.resetFields();
      setEditUserModalVisible(false);
      setSelectedUserToEdit(null);
      showSuccess('User updated successfully!');
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  const handleDeleteUserClick = useCallback((user) => {
    setSelectedUserToDelete(user);
    setDeleteUserModalVisible(true);
  }, []);

  const handleDeleteUserConfirm = async () => {
    if (!selectedUserToDelete) return;
    try {
      await deleteUser(selectedUserToDelete.id).unwrap();
      showSuccess('User deleted successfully!');
      setDeleteUserModalVisible(false);
      setSelectedUserToDelete(null);
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const columns = useMemo(
    () =>
      getUserTableColumns({
        onEditUser: handleEditUserClick,
        onDeleteUser: handleDeleteUserClick,
        timezone,
      }),
    [handleEditUserClick, handleDeleteUserClick, timezone]
  );

  return (
    <>
      <Card
        title={
          <Space>
            <UserOutlined />
            Users
          </Space>
        }
        extra={
          <Button
            variant="dashed"
            color="green"
            icon={<PlusOutlined />}
            onClick={() => setInviteUserModalVisible(true)}
          >
            Invite New User
          </Button>
        }
        className="page-card"
        style={{ marginBottom: 24 }}
        styles={{ body: { padding: '1px 0 0' } }}
      >
        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={isLoading || isFetching}
          onChange={handleTableChange}
          pagination={paginationConfig(totalElements)}
        />
      </Card>

      <InviteUserModal
        open={inviteUserModalVisible}
        onCancel={() => {
          setInviteUserModalVisible(false);
          userForm.resetFields();
        }}
        onSubmit={handleInviteUser}
        loading={isInviting}
        form={userForm}
      />

      <EditUserModal
        open={editUserModalVisible}
        onCancel={() => {
          setEditUserModalVisible(false);
          setSelectedUserToEdit(null);
          editUserForm.resetFields();
        }}
        onSubmit={handleEditUser}
        loading={isUpdating}
        form={editUserForm}
      />

      {deleteUserModalVisible && (
        <DeleteUserModal
          open={deleteUserModalVisible}
          onOk={handleDeleteUserConfirm}
          onCancel={() => {
            setDeleteUserModalVisible(false);
            setSelectedUserToDelete(null);
          }}
          confirmLoading={isDeleting}
          selectedUser={selectedUserToDelete}
        />
      )}
    </>
  );
};

UsersTable.propTypes = {
  portfolioId: PropTypes.string,
};

export default UsersTable;
